// TriptychHero — Meetstream-style full-bleed hero | 3 product cards fill the entire viewport as background art | headline + CTAs float centered with dark vignette | prefers-reduced-motion safe

import { useState, useEffect } from "react";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { motion, AnimatePresence, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { X } from "lucide-react";
import FeatureMarquee from "./FeatureMarquee";
import { CALENDLY_URL } from "../../constants";

const EASE = [0.22, 1, 0.36, 1] as const;

// Height of the ticker band pinned at the hero's bottom edge:
// FeatureMarquee py-6 (48) + pill py-2/text-sm (~36) + wrapper padding (16) + border.
// The card layer reserves this much bottom space so the frames terminate cleanly
// above the ribbon instead of colliding with it.
const RIBBON_HEIGHT = 104;

// Hero frame width. Shared so the gap compensation below tracks it exactly.
const FRAME_WIDTH = "clamp(190px, 19.5vw, 320px)";

// transform:scale does not affect layout, so flex distributes the UNSCALED
// boxes evenly while each card's visible edge sits (1 - scale)/2 * width
// inward. Without correction the gaps come out uneven (14.6px vs 23.0px at
// 1440). This margin cancels the difference per card, so all three visual gaps
// equal the flex gap at every viewport width.
const gapFix = (scale: number) => `calc(${FRAME_WIDTH} * ${(-(1 - scale) / 2).toFixed(4)})`;

// ─── AnimatedEllipsis ─────────────────────────────────────────────────────────

function AnimatedEllipsis({ prefersReduced }: { prefersReduced: boolean }) {
  const [dots, setDots] = useState('');

  useEffect(() => {
    if (prefersReduced) return;
    const id = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : prev + '.'));
    }, 400);
    return () => clearInterval(id);
  }, [prefersReduced]);

  return (
    <span style={{ display: 'inline-block', minWidth: '1.5em' }}>
      {prefersReduced ? '...' : dots}
    </span>
  );
}

// ─── HeroBadge ────────────────────────────────────────────────────────────────

function HeroBadge() {
  const prefersReduced = useReducedMotion();

  return (
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      border: "1px solid rgba(255,255,255,0.2)",
      borderRadius: 999,
      padding: "5px 14px",
      background: "rgba(255,255,255,0.04)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
    }}>
      <span className="relative flex h-2 w-2 shrink-0">
        {!prefersReduced && (
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
        )}
        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
      </span>
      <span style={{ fontSize: "10px", letterSpacing: "0.18em", color: "rgba(255,255,255,0.65)", textTransform: "uppercase", whiteSpace: "nowrap" }}>
        Advanced intelligence in play
      </span>
    </div>
  );
}

// ─── TriptychHeroProps ────────────────────────────────────────────────────────

interface TriptychHeroProps {
  onEnterStudent: () => void;
  onEnterCounselor: () => void;
  onTryItNow?: () => void;
}

// ─── Card configuration ───────────────────────────────────────────────────────

const CARD_CONFIGS = [
  {
    key: "student" as const,
    // Slightly forward: lower Y offset, marginally larger scale, stronger shadow
    translateY: 20,
    cardScale: 1.02,
    zIndex: 2,
    border: "rgba(168, 85, 247, 0.6)",
    hoverBorder: "rgba(168, 85, 247, 0.7)",
    chromeBorderColor: "rgba(168, 85, 247, 0.2)",
    shadow: "0 16px 48px rgba(0,0,0,0.6), 0 0 40px rgba(168,85,247,0.25)",
    hoverShadow: "0 24px 56px rgba(0,0,0,0.7), 0 0 50px rgba(168,85,247,0.45), 0 0 90px rgba(168,85,247,0.18)",
    delay: 0,
  },
  {
    key: "counselor" as const,
    // Mid depth
    translateY: 45,
    cardScale: 0.99,
    zIndex: 2,
    border: "rgba(236, 72, 153, 0.5)",
    hoverBorder: "rgba(236, 72, 153, 0.7)",
    chromeBorderColor: "rgba(236, 72, 153, 0.2)",
    shadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 24px rgba(236,72,153,0.18)",
    hoverShadow: "0 20px 48px rgba(0,0,0,0.65), 0 0 40px rgba(236,72,153,0.38), 0 0 80px rgba(236,72,153,0.14)",
    delay: 0.1,
  },
  {
    key: "college" as const,
    // Furthest back: highest Y offset, smallest scale, softest shadow
    translateY: 70,
    cardScale: 0.96,
    zIndex: 1,
    border: "rgba(230, 168, 23, 0.45)",
    hoverBorder: "rgba(230, 168, 23, 0.7)",
    chromeBorderColor: "rgba(230, 168, 23, 0.2)",
    shadow: "0 6px 26px rgba(0,0,0,0.45), 0 0 20px rgba(230,168,23,0.15)",
    hoverShadow: "0 18px 44px rgba(0,0,0,0.6), 0 0 36px rgba(230,168,23,0.34), 0 0 72px rgba(230,168,23,0.12)",
    delay: 0.2,
  },
] as const;

// ─── HeroCardLayer ────────────────────────────────────────────────────────────
// Background frame layer — front frame fully visible, rear frame bleeds past the
// right edge as a deliberate composition | perspective tilt from top | parallax on scroll

interface HeroCardLayerProps {
  prefersReduced: boolean;
  isTablet: boolean;
}

function HeroCardLayer({ prefersReduced, isTablet }: HeroCardLayerProps) {

  // Scroll parallax — the two frames drift at different rates, capped at ~20px
  const { scrollY } = useScroll();
  const parallaxSlow = useTransform(scrollY, [0, 600], [0, -12]);
  const parallaxFast = useTransform(scrollY, [0, 600], [0, -20]);

  const mockups: Record<"student" | "counselor" | "college", React.ReactNode> = {
    student: <StudentMockup />,
    counselor: <CounselorMockup />,
    college: <CollegeMockup />,
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        // Composed bleed: the front (student) frame sits fully inside the
        // column; only the rear (counselor) frame runs past the right edge.
        // Cluster pulled left (~72px at 1440) so the gold frame has breathing
        // room on the right instead of crowding the viewport edge.
        paddingLeft: 0,
        marginLeft: -72,
        // Reserve the ribbon band plus a clear gap, so no frame bottom edge
        // touches or overlaps it.
        paddingBottom: RIBBON_HEIGHT + 48,
        transform: "perspective(1200px) rotateX(8deg)",
        transformOrigin: "center top",
        width: "100%",
        height: "100%",
        zIndex: 0,
      }}
      aria-hidden="true"
    >
      {/* marginTop trimmed from 60 → 28 so the frames sit higher and extend
          further down toward the ticker, anchoring the composition */}
      <div style={{ display: "flex", gap: "16px", alignItems: "stretch", marginTop: 28 }}>
        {CARD_CONFIGS.map((card, cardIndex) => (
          // Parallax wrapper — drifts at a different rate per card so the frames
          // separate slightly on scroll. Kept on a wrapper because the card
          // itself already animates `y` for entrance and hover.
          <motion.div
            key={`${card.key}-parallax`}
            style={{
              y: prefersReduced ? 0 : cardIndex === 0 ? parallaxSlow : parallaxFast,
              // Cancels the layout/visual mismatch introduced by cardScale so
              // the three frames sit at equal horizontal gaps.
              marginLeft: gapFix(card.cardScale),
              marginRight: gapFix(card.cardScale),
            }}
          >
          {/* Entrance lives on this wrapper — rise + fade, 600ms, staggered
              150ms. Keeping it here means the inner card's `y` is free to serve
              hover alone, instead of inheriting the entrance delay (which is
              what made hover take ~2s to respond). */}
          <motion.div
            initial={prefersReduced ? {} : { opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              ease: EASE,
              delay: prefersReduced ? 0 : 0.35 + cardIndex * 0.15,
            }}
          >
          {/* Ambient float — its own layer so it composites with the parallax
              above and the hover below without either fighting it. Each frame
              gets a different cycle offset so they drift independently rather
              than sliding as one block. */}
          <motion.div
            animate={prefersReduced ? {} : { y: [0, -10, 0] }}
            transition={
              prefersReduced
                ? { duration: 0 }
                : {
                    duration: 7,
                    ease: 'easeInOut',
                    repeat: Infinity,
                    repeatType: 'reverse',
                    delay: cardIndex * 1.2,
                  }
            }
          >
          <motion.div
            key={card.key}
            initial={prefersReduced ? {} : { scale: 0.96, filter: "blur(8px)" }}
            animate={{ y: card.translateY, scale: card.cardScale, filter: "blur(0px)", boxShadow: card.shadow }}
            whileHover={prefersReduced ? {} : {
              y: card.translateY - 14,
              boxShadow: card.hoverShadow,
            }}
            transition={{
              // Hover-only: responds instantly, no entrance delay to wait out
              duration: 0.18,
              ease: EASE,
              // Scale/blur settle once on mount alongside the wrapper's rise
              scale: { duration: 0.6, ease: EASE, delay: prefersReduced ? 0 : 0.35 + cardIndex * 0.15 },
              filter: { duration: 0.6, ease: EASE, delay: prefersReduced ? 0 : 0.35 + cardIndex * 0.15 },
            }}
            style={{
              // Narrowed so three frames fit the right column; the rightmost
              // still bleeds past the edge as a composed crop.
              width: FRAME_WIDTH,
              // Sized to end clearly above the ribbon. The layer reserves
              // RIBBON_HEIGHT + 48; capping height here keeps a visible gap at
              // 1024 too, where vertical space is tightest.
              height: isTablet ? '46vh' : 'clamp(340px, 56vh, 620px)',
              borderRadius: "16px",
              flexShrink: 0,
              pointerEvents: "auto",
              border: `1px solid ${card.border}`,
              background: card.key === "counselor" ? "rgba(8, 2, 20, 0.85)" : "rgba(8, 3, 18, 0.78)",
              backdropFilter: "blur(14px)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              zIndex: card.zIndex,
            }}
          >
            {/* Browser chrome bar */}
            <div
              style={{
                height: 36,
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "0 12px",
                borderBottom: `1px solid ${card.chromeBorderColor}`,
                background: "rgba(255,255,255,0.04)",
                borderRadius: "16px 16px 0 0",
                flexShrink: 0,
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "rgba(255,100,100,0.6)", flexShrink: 0, display: "inline-block" }} />
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "rgba(255,200,0,0.6)", flexShrink: 0, display: "inline-block" }} />
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "rgba(100,200,100,0.6)", flexShrink: 0, display: "inline-block" }} />
              <div style={{ flex: 1, marginLeft: 12, height: 14, borderRadius: 99, background: "rgba(255,255,255,0.06)", maxWidth: 130 }} />
            </div>
            {/* Mockup content */}
            <div style={{ flex: 1, padding: 14, overflow: "hidden", opacity: 0.75 }}>
              {mockups[card.key]}
            </div>
          </motion.div>
          </motion.div>
          </motion.div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─── TriptychHero ─────────────────────────────────────────────────────────────

function TriptychHero({ onEnterStudent, onEnterCounselor, onTryItNow }: TriptychHeroProps) {
  const prefersReduced = useReducedMotion();
  const width = useWindowWidth();
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const [roleModalOpen, setRoleModalOpen] = useState(false);

  const handleSeeHowItWorks = () => {
    const el = document.getElementById("problem-section");
    if (el) el.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth" });
  };

  return (
    <div style={{ background: '#050508', contain: 'paint' }}>
    <section className="relative z-10 w-full" style={isMobile ? { minHeight: '100vh' } : { height: "100vh", maxHeight: "100vh", overflow: "hidden" }}>

      {/* Top separator line */}
      <div
        className="absolute top-0 left-0 right-0 h-px pointer-events-none"
        style={{ background: "rgba(255,255,255,0.04)" }}
        aria-hidden="true"
      />

      {/* ══ DESKTOP — two-column split ═══════════════════════════════════════ */}

      <div
        className="hidden md:flex"
        style={{ height: "100vh", alignItems: "center", position: "relative" }}
      >
        {/* Depth vignette — darker edges, faintly lifted centre, so the frames
            and text sit IN a space rather than on flat black. Texture, not a
            spotlight: kept under 0.05 alpha at every stop. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
            background:
              "radial-gradient(ellipse 90% 75% at 50% 45%, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.012) 38%, transparent 62%), radial-gradient(ellipse 120% 100% at 50% 50%, transparent 55%, rgba(0,0,0,0.42) 100%)",
          }}
        />

        {/* Ambient purple glow — single soft orb spanning the whole hero */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "10%",
            left: "20%",
            width: "70%",
            height: "80%",
            background: "radial-gradient(ellipse at 40% 50%, rgba(109,40,217,0.13) 0%, rgba(109,40,217,0.04) 45%, transparent 70%)",
            filter: "blur(60px)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* LEFT column — text, clean background, no mockup behind it */}
        <div
          style={{
            width: "45%",
            flexShrink: 0,
            paddingLeft: "8%",
            paddingRight: "4%",
            position: "relative",
            zIndex: 20,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          {/* Ambient violet glow behind the headline block. Reuses the existing
              glowPulse keyframe (index.css) per CLAUDE.md §6 — no new animation. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-8%",
              top: "22%",
              width: "560px",
              height: "360px",
              background:
                "radial-gradient(ellipse at center, rgba(139,92,246,0.22) 0%, rgba(124,58,237,0.08) 45%, transparent 72%)",
              filter: "blur(60px)",
              pointerEvents: "none",
              zIndex: 0,
              animation: prefersReduced ? "none" : "glowPulse 10s ease-in-out infinite",
            }}
          />

          {/* Eyebrow — names the buyer before the headline names the category */}
          <motion.div
            initial={prefersReduced ? {} : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE, delay: prefersReduced ? 0 : 0 }}
            style={{
              position: "relative",
              zIndex: 1,
              marginBottom: 18,
              display: "inline-flex",
              alignItems: "center",
              padding: "5px 13px",
              borderRadius: 999,
              // Gold reads only as a thin dim outline — the label itself uses the
              // muted small-caps tone used elsewhere, so it never looks yellow.
              border: "1px solid rgba(230,168,23,0.38)",
              background: "rgba(230,168,23,0.035)",
              fontFamily: "Inter, sans-serif",
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(196,181,253,0.82)",
              whiteSpace: "nowrap",
            }}
          >
            For counseling firms &amp; schools
          </motion.div>

          {/* Headline */}
          <h1 className="font-serif font-medium tracking-tight m-0 p-0 overflow-visible"
            style={{ fontSize: "clamp(2.4rem, 3.2vw, 3.4rem)", lineHeight: 0.9, position: "relative", zIndex: 1 }}
          >
            <motion.div
              style={{ color: "#FFFFFF", whiteSpace: "nowrap" }}
              initial={prefersReduced ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: EASE, delay: prefersReduced ? 0 : 0.1 }}
            >The AI workspace for</motion.div>
            <motion.div
              style={{ color: "#a855f7", fontStyle: "italic", paddingBottom: '6px', whiteSpace: "nowrap" }}
              initial={prefersReduced ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: EASE, delay: prefersReduced ? 0 : 0.2 }}
            >college counseling.</motion.div>
          </h1>

          {/* Subheadline — two beats, not one dense block. Same wording. */}
          <motion.div
            className="font-sans text-base md:text-lg mt-4 mb-0"
            style={{
              color: "rgba(255,255,255,0.75)",
              // Narrower measure + balanced wrapping so both paragraphs form
              // even blocks instead of ragged fragments.
              maxWidth: 430,
              lineHeight: 1.75,
              textWrap: "balance",
            }}
            initial={prefersReduced ? {} : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: prefersReduced ? 0 : 0.2 }}
          >
            <p style={{ margin: 0, textWrap: "balance" }}>
              Counseling firms and schools run every student's application in one place — essays, deadlines, college lists, progress.
            </p>
            <p style={{ margin: '10px 0 0', textWrap: "balance" }}>
              Students and counselors work together. Leadership finally sees everything.
            </p>
          </motion.div>

          {/* CTA button */}
          <motion.div
            className="flex flex-wrap gap-3 mt-4"
            style={{ pointerEvents: "auto" }}
            initial={prefersReduced ? {} : { opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: EASE, delay: prefersReduced ? 0 : 0.3 }}
          >
            <motion.a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="landing-cta px-7 py-3.5 text-sm"
              style={{ textDecoration: 'none', display: 'inline-block' }}
              whileHover={prefersReduced ? {} : {
                scale: 1.02,
                y: -2,
                boxShadow: "0 12px 40px rgba(139,92,246,0.6), 0 0 0 1px rgba(139,92,246,0.42)",
                transition: { duration: 0.15, ease: EASE },
              }}
              whileTap={prefersReduced ? {} : { scale: 0.98 }}
            >
              Book a Demo →
            </motion.a>
          </motion.div>

          {/* Trust line + counselor link */}
          <motion.div
            style={{ pointerEvents: "auto" }}
            initial={prefersReduced ? {} : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.55, ease: EASE, delay: prefersReduced ? 0 : 0.4 }}
          >
            <div style={{ marginTop: '12px' }}>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.62)', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>
                Essays · Deadlines · College lists · Progress
              </p>
            </div>
            <div style={{ marginTop: '8px' }}>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-secondary"
              >
                I'm a student — book a free intro call →
              </a>
            </div>
          </motion.div>
        </div>

        {/* RIGHT column — mockups, ambient glows, bottom fade */}
        <div
          style={{ flex: 1, height: "100%", position: "relative" }}
          aria-hidden="true"
        >
          {/* Ambient glows behind cards */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
            {/* Purple glow — behind student card */}
            <div style={{
              position: 'absolute',
              left: '5%',
              top: '20%',
              width: '420px',
              height: '520px',
              background: 'radial-gradient(ellipse at center, rgba(168,85,247,0.25) 0%, rgba(168,85,247,0.08) 40%, transparent 70%)',
              filter: 'blur(40px)',
              willChange: 'transform',
              transform: 'translateZ(0)',
            }} />
            {/* Pink glow — behind counselor card */}
            <div style={{
              position: 'absolute',
              left: '55%',
              top: '15%',
              transform: 'translateX(-50%) translateZ(0)',
              width: '500px',
              height: '600px',
              background: 'radial-gradient(ellipse at center, rgba(236,72,153,0.2) 0%, rgba(236,72,153,0.06) 40%, transparent 70%)',
              filter: 'blur(50px)',
              willChange: 'transform',
            }} />
          </div>

          {/* Cards */}
          <div style={{ position: 'absolute', inset: 0, zIndex: 5 }}>
            <HeroCardLayer prefersReduced={!!prefersReduced} isTablet={isTablet} />
          </div>

          {/* Bottom fade */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              // Shorter fade — 220px swallowed the lower third of the frames and
              // read as dead black space. Now it just softens into the ticker.
              height: '120px',
              background: 'linear-gradient(to bottom, transparent, rgba(5,5,8,0.9))',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* Ticker — pinned to the hero's bottom edge, inside the hero's own
            stacking context so it can never paint over a later section.
            Reads as "everything that lives in the workspace." */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 15,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(5,5,8,0.72)',
            backdropFilter: 'blur(8px)',
            paddingTop: 8,
            paddingBottom: 8,
          }}
        >
          <FeatureMarquee />
        </div>
      </div>

      {/* ══ MOBILE ═══════════════════════════════════════════════════════════ */}

      <div className="md:hidden flex flex-col">
        {/* Text block */}
        <div className="pt-28 px-4 md:px-8 pb-12 md:pb-16 text-center flex flex-col items-center gap-6">
          <HeroBadge />

          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.02em', overflow: 'visible', paddingBottom: 12, margin: 0 }}
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl w-full"
          >
            <span className="block" style={{ color: "#FFFFFF" }}>The AI workspace for</span>
            <span className="block" style={{ color: "#a855f7", fontStyle: "italic", paddingBottom: '8px' }}>college counseling.</span>
          </h1>

          <div
            className="font-sans text-sm md:text-base lg:text-lg text-white/75 w-full max-w-full lg:max-w-lg"
            style={{ lineHeight: 1.75, textWrap: "balance" }}
          >
            <p style={{ margin: 0, textWrap: "balance" }}>
              Counseling firms and schools run every student's application in one place — essays, deadlines, college lists, progress.
            </p>
            <p style={{ margin: '10px 0 0', textWrap: "balance" }}>
              Students and counselors work together. Leadership finally sees everything.
            </p>
          </div>

          <div className="flex flex-col items-center gap-0 w-full">
            <div className="flex flex-wrap gap-4 justify-center w-full">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-cta px-7 py-3.5 text-sm w-full md:w-auto"
                style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}
              >
                Book a Demo →
              </a>
            </div>
            <div style={{ marginTop: '14px', textAlign: 'center' }}>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.62)', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                Essays · Deadlines · College lists · Progress
              </p>
            </div>
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-secondary"
              >
                I'm a student — book a free intro call →
              </a>
            </div>
          </div>
        </div>

        {/* Browser mockup card — counselor UI scaled to fit */}
        <div style={{ padding: '0 24px 60px' }}>
          <div style={{
            position: 'relative',
            width: '100%',
            height: 200,
            borderRadius: 12,
            border: '1px solid rgba(168,85,247,0.6)',
            background: 'rgba(8,3,18,0.72)',
            backdropFilter: 'blur(8px)',
            overflow: 'hidden',
          }}>
            {/* Chrome bar */}
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0,
              height: 32,
              display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px',
              borderBottom: '1px solid rgba(168,85,247,0.2)',
              background: 'rgba(255,255,255,0.03)',
              zIndex: 1,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,100,100,0.5)', display: 'inline-block' }} />
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,200,0,0.5)', display: 'inline-block' }} />
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(100,200,100,0.5)', display: 'inline-block' }} />
              <div style={{ flex: 1, marginLeft: 8, height: 12, borderRadius: 99, background: 'rgba(255,255,255,0.06)', maxWidth: 100 }} />
            </div>
            {/* Scaled mockup content */}
            <div style={{ position: 'absolute', top: 32, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
              <div style={{ transform: 'scale(0.6)', transformOrigin: 'top left', width: '167%' }}>
                <CounselorMockup />
              </div>
            </div>
          </div>
        </div>

        {/* Ticker — in normal flow at the end of the mobile hero (the mobile
            hero scrolls, so a pinned strip would be wrong here) */}
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            paddingTop: 8,
            paddingBottom: 8,
          }}
        >
          <FeatureMarquee />
        </div>
      </div>

      <RoleModal
        isOpen={roleModalOpen}
        onClose={() => setRoleModalOpen(false)}
        onEnterStudent={onEnterStudent}
        onEnterCounselor={onEnterCounselor}
      />
    </section>
    </div>
  );
}

// ─── BrowserFrame ─────────────────────────────────────────────────────────────
// Used for the mobile single-card preview only

interface BrowserFrameProps {
  accentColor: string;
  glowColor: string;
  glowSize?: number;
  hoverGlowColor?: string;
  isHovered: boolean;
  isCollege?: boolean;
  children: React.ReactNode;
}

function BrowserFrame({
  accentColor,
  glowColor,
  glowSize = 40,
  hoverGlowColor = "rgba(109,40,217,0.35)",
  isHovered,
  isCollege,
  children,
}: BrowserFrameProps) {
  const hoverShadow = `0 30px 80px rgba(0,0,0,0.7), 0 0 ${isCollege ? 40 : 60}px ${hoverGlowColor}, inset 0 1px 0 rgba(255,255,255,0.07)`;

  return (
    <div
      className="rounded-xl overflow-hidden flex flex-col"
      style={{
        background: "#0D0B1A",
        border: `1px solid ${accentColor}`,
        borderRadius: "12px",
        boxShadow: isHovered
          ? hoverShadow
          : `0 25px 60px rgba(0,0,0,0.5), 0 0 ${glowSize}px ${glowColor}, inset 0 1px 0 rgba(255,255,255,0.05)`,
        transition: "box-shadow 0.35s cubic-bezier(0.22,1,0.36,1), border-color 0.35s cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      <div
        className="flex items-center gap-1.5 px-3 py-2.5 border-b flex-shrink-0"
        style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}
      >
        <span className="w-[7px] h-[7px] rounded-full" style={{ background: "#FF5F57" }} />
        <span className="w-[7px] h-[7px] rounded-full" style={{ background: "#FEBC2E" }} />
        <span className="w-[7px] h-[7px] rounded-full" style={{ background: "#28C840" }} />
        <div className="flex-1 ml-3 h-3.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(8px)", maxWidth: "130px" }} />
      </div>
      <div className="flex-1 p-3.5">{children}</div>
    </div>
  );
}

// ─── StudentMockup ────────────────────────────────────────────────────────────

function StudentMockup() {
  const prefersReduced = useReducedMotion();
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    if (prefersReduced) { setAnimated(true); return; }
    const t = setTimeout(() => setAnimated(true), 400);
    return () => clearTimeout(t);
  }, [prefersReduced]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 0 }}>
      {/* Application Health */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: 10 }}>
        <svg width="40" height="40" viewBox="0 0 40 40" style={{ flexShrink: 0 }}>
          <circle cx="20" cy="20" r="14" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="4" />
          <circle cx="20" cy="20" r="14" fill="none"
            stroke="url(#pg)" strokeWidth="4" strokeLinecap="round"
            strokeDasharray={`${0.78 * 87.96} ${87.96}`} strokeDashoffset="21.99"
            transform="rotate(-90 20 20)"
          />
          <defs>
            <linearGradient id="pg" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#EC4899" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
          <text x="20" y="23.5" textAnchor="middle" style={{ fontSize: "7px", fill: "#F8F7FF", fontFamily: "Inter,sans-serif", fontWeight: 700 }}>78%</text>
        </svg>
        <div>
          <div style={{ fontSize: 10, color: "#F8F7FF", fontWeight: 600, fontFamily: "Inter,sans-serif" }}>Application Health</div>
          <div style={{ fontSize: 7, color: "rgba(255,255,255,0.4)", fontFamily: "Inter,sans-serif", marginTop: 2 }}>Common App — on track</div>
        </div>
      </div>

      {/* Stat chips */}
      <div style={{ display: "flex", gap: 6, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: 10 }}>
        {[
          { label: "Colleges", value: "12", color: "rgba(168,85,247,0.9)" },
          { label: "Essays", value: "4", color: "rgba(236,72,153,0.9)" },
          { label: "Activities", value: "8", color: "rgba(251,191,36,0.85)" },
        ].map((s) => (
          <div key={s.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", borderRadius: 6, padding: "6px 0", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: s.color, fontFamily: "Inter,sans-serif", lineHeight: 1 }}>{s.value}</span>
            <span style={{ fontSize: 6, color: "rgba(255,255,255,0.35)", fontFamily: "Inter,sans-serif", marginTop: 2 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* My Colleges */}
      <div style={{ paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: 10 }}>
        <div style={{ fontSize: 7, color: "rgba(255,255,255,0.55)", fontFamily: "Inter,sans-serif", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5 }}>My Colleges</div>
        {[
          { name: "Harvard",  match: "91%", statusColor: "rgba(168,85,247,1)",    statusLabel: "Reach"  },
          { name: "Stanford", match: "88%", statusColor: "rgba(168,85,247,1)",    statusLabel: "Reach"  },
          { name: "MIT",      match: "85%", statusColor: "rgba(251,191,36,0.9)",  statusLabel: "Target" },
          { name: "Yale",     match: "79%", statusColor: "rgba(74,222,128,0.9)",  statusLabel: "Likely" },
        ].map((c) => (
          <div key={c.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: 5, padding: "4px 8px", background: "rgba(255,255,255,0.02)", marginBottom: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: c.statusColor, display: "inline-block", flexShrink: 0 }} />
              <span style={{ fontSize: 8, color: "rgba(255,255,255,0.7)", fontFamily: "Inter,sans-serif" }}>{c.name}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 7, color: "rgba(255,255,255,0.3)", fontFamily: "Inter,sans-serif" }}>{c.statusLabel}</span>
              <span style={{ fontSize: 8, color: "rgba(168,85,247,0.9)", fontFamily: "Inter,sans-serif", fontWeight: 600 }}>{c.match}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Essays */}
      <div style={{ paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: 10 }}>
        <div style={{ fontSize: 7, color: "rgba(255,255,255,0.55)", fontFamily: "Inter,sans-serif", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5 }}>Essays</div>
        {[
          { label: "Common App Essay",   pct: 78, color: "linear-gradient(to right, #EC4899, #8B5CF6)" },
          { label: "Stanford — Why Us?", pct: 45, color: "linear-gradient(to right, #8B5CF6, #A78BFA)" },
          { label: "MIT Supplement",     pct: 20, color: "rgba(167,139,250,1)" },
        ].map((e, i) => (
          <div key={e.label} style={{ marginBottom: 5 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ fontSize: 7, color: "rgba(255,255,255,0.5)", fontFamily: "Inter,sans-serif" }}>{e.label}</span>
              <span style={{ fontSize: 7, color: "rgba(168,85,247,0.9)", fontFamily: "Inter,sans-serif", fontWeight: 600 }}>{e.pct}%</span>
            </div>
            <div style={{ height: 3, borderRadius: 99, background: "rgba(255,255,255,0.05)", width: "100%" }}>
              {/* Staggered fill — bars land in sequence over ~600-900ms */}
              <div style={{
                height: 3,
                borderRadius: 99,
                background: e.color,
                width: animated ? `${e.pct}%` : "0%",
                transition: prefersReduced ? "none" : "width 0.7s cubic-bezier(0.22,1,0.36,1)",
                transitionDelay: prefersReduced ? "0ms" : `${i * 110}ms`,
              }} />
            </div>
          </div>
        ))}
      </div>

      {/* Today's Focus */}
      <div>
        <div style={{ fontSize: 7, color: "rgba(255,255,255,0.55)", fontFamily: "Inter,sans-serif", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5 }}>Today's Focus</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {["Common App Essay", "Stanford Deadline", "Fee Waiver"].map((chip, i) => (
            // The deadline chip pulses its glow on a slow loop — one living
            // element rather than a whole animated dashboard.
            <motion.span
              key={chip}
              animate={prefersReduced || i !== 1 ? {} : {
                boxShadow: [
                  "0 0 0px rgba(139,92,246,0)",
                  "0 0 10px rgba(139,92,246,0.55)",
                  "0 0 0px rgba(139,92,246,0)",
                ],
              }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
              style={{ fontSize: 7, color: "rgba(167,139,250,0.85)", fontFamily: "Inter,sans-serif", padding: "2px 7px", borderRadius: 99, background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.2)" }}
            >
              {chip}
            </motion.span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── CounselorMockup ──────────────────────────────────────────────────────────

function CounselorMockup() {
  const prefersReduced = useReducedMotion();
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    if (prefersReduced) { setAnimated(true); return; }
    const t = setTimeout(() => setAnimated(true), 400);
    return () => clearTimeout(t);
  }, [prefersReduced]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 0 }}>
      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: 10 }}>
        {[
          { label: "Students",       value: "47",  color: "rgba(236,72,153,0.9)"  },
          { label: "Cohort Progress", value: "64%", color: "rgba(236,72,153,0.7)"  },
          { label: "Pending Review", value: "12",  color: "rgba(251,191,36,0.85)" },
          { label: "Urgent",         value: "3",   color: "rgba(239,68,68,0.85)"  },
        ].map((s) => (
          <div key={s.label} style={{ display: "flex", flexDirection: "column", borderRadius: 6, padding: "6px 8px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: s.color, fontFamily: "Inter,sans-serif", lineHeight: 1 }}>{s.value}</span>
            <span style={{ fontSize: 6, color: "rgba(255,255,255,0.35)", fontFamily: "Inter,sans-serif", marginTop: 3 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Student roster */}
      <div style={{ paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: 10 }}>
        <div style={{ fontSize: 7, color: "rgba(255,255,255,0.55)", fontFamily: "Inter,sans-serif", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5 }}>Student Roster</div>
        {[
          { name: "Sarah K.",  pct: 82, status: "On Track",     statusColor: "rgba(74,222,128,0.9)",  barColor: "linear-gradient(to right, #10B981, #34D399)" },
          { name: "Marcus L.", pct: 61, status: "Needs Review", statusColor: "rgba(251,191,36,0.9)",  barColor: "linear-gradient(to right, #F59E0B, #FCD34D)" },
          { name: "Priya R.",  pct: 43, status: "Behind",       statusColor: "rgba(239,68,68,0.85)",  barColor: "rgba(239,68,68,1)"                            },
        ].map((s, i) => (
          <div key={s.name} style={{ borderRadius: 6, padding: "6px 8px", background: "rgba(255,255,255,0.02)", marginBottom: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 8, color: "rgba(255,255,255,0.75)", fontFamily: "Inter,sans-serif", fontWeight: 500 }}>{s.name}</span>
              <span style={{ fontSize: 6, color: s.statusColor, fontFamily: "Inter,sans-serif", fontWeight: 500 }}>{s.status}</span>
            </div>
            <div style={{ height: 3, borderRadius: 99, background: "rgba(255,255,255,0.05)", width: "100%" }}>
              <div style={{
                height: 3,
                borderRadius: 99,
                background: s.barColor,
                width: animated ? `${s.pct}%` : "0%",
                transition: prefersReduced ? "none" : "width 0.7s cubic-bezier(0.22,1,0.36,1)",
                transitionDelay: prefersReduced ? "0ms" : `${i * 110}ms`,
              }} />
            </div>
            <div style={{ fontSize: 6, color: "rgba(255,255,255,0.25)", fontFamily: "Inter,sans-serif", marginTop: 2 }}>{s.pct}% complete</div>
          </div>
        ))}
      </div>

      {/* Cohort bar */}
      <div style={{ paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
          <span style={{ fontSize: 7, color: "rgba(255,255,255,0.55)", fontFamily: "Inter,sans-serif" }}>Cohort progress</span>
          <span style={{ fontSize: 7, color: "rgba(236,72,153,0.9)", fontFamily: "Inter,sans-serif", fontWeight: 600 }}>64%</span>
        </div>
        <div style={{ height: 4, borderRadius: 99, background: "rgba(255,255,255,0.06)", width: "100%" }}>
          <div style={{
            height: 4,
            borderRadius: 99,
            background: "linear-gradient(to right, #EC4899, #F472B6)",
            width: animated ? "64%" : "0%",
            transition: prefersReduced ? "none" : "width 0.7s cubic-bezier(0.22,1,0.36,1)",
            transitionDelay: prefersReduced ? "0ms" : "330ms",
          }} />
        </div>
        <div style={{ fontSize: 6, color: "rgba(255,255,255,0.25)", fontFamily: "Inter,sans-serif", marginTop: 3 }}>Smart triage active — 3 students flagged</div>
      </div>

      {/* Quick actions */}
      <div>
        <div style={{ fontSize: 7, color: "rgba(255,255,255,0.55)", fontFamily: "Inter,sans-serif", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5 }}>Quick Actions</div>
        <div style={{ display: "flex", gap: 5 }}>
          {["Review Essays", "Send Reminder", "Export Report"].map((a) => (
            <div key={a} style={{ flex: 1, textAlign: "center", borderRadius: 6, padding: "5px 0", background: "rgba(236,72,153,0.07)", border: "1px solid rgba(236,72,153,0.15)" }}>
              <span style={{ fontSize: 6, color: "rgba(236,72,153,0.8)", fontFamily: "Inter,sans-serif", fontWeight: 500 }}>{a}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── CollegeMockup ────────────────────────────────────────────────────────────

function CollegeMockup() {
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 400);
    return () => clearTimeout(t);
  }, []);

  const gold = "#e6a817";
  const font = "Inter,sans-serif";
  // Exact tokens from student/counselor cards
  const sectionLbl: React.CSSProperties = { fontSize: 7, color: "rgba(255,255,255,0.55)", fontFamily: font, textTransform: "uppercase", letterSpacing: "0.12em" };
  const divider = "1px solid rgba(255,255,255,0.05)";
  const barTrack: React.CSSProperties = { height: 3, borderRadius: 99, background: "rgba(255,255,255,0.05)", width: "100%" };
  const animBar = (pct: number, fill: string): React.CSSProperties => ({
    height: 3, borderRadius: 99, background: fill,
    width: animated ? `${pct}%` : "0%",
    transition: "width 1.2s ease",
  });

  return (
    <div style={{ transform: "scale(0.85)", transformOrigin: "top left", width: "118%" }}>
      <style>{`@keyframes schPulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>

      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10, overflow: "hidden", fontFamily: font }}>

        {/* HEADER ROW */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
          <span style={{ fontSize: 10, color: "#F8F7FF", fontWeight: 600, fontFamily: font }}>High School · Class of 20XX</span>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", display: "inline-block", flexShrink: 0, animation: "schPulse 1.5s ease-in-out infinite" }} />
            <span style={{ fontSize: 7, color: "#4ade80", fontFamily: font, fontWeight: 600 }}>Live</span>
          </div>
        </div>

        {/* 4 STAT PILLS — matching counselor's stats grid exactly */}
        <div style={{ display: "flex", gap: 6, paddingBottom: 10, borderBottom: divider }}>
          {[
            { value: "247", label: "Batch Size",  color: "#ffffff"  },
            { value: "89%", label: "On Track",    color: gold       },
            { value: "18",  label: "At Risk",     color: "#f87171"  },
            { value: "6",   label: "Counselors",  color: "#ffffff"  },
          ].map((p) => (
            <div key={p.label} style={{ flex: 1, display: "flex", flexDirection: "column", borderRadius: 6, padding: "6px 8px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)", textAlign: "center", gap: 3 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: p.color, fontFamily: font, lineHeight: 1 }}>{p.value}</span>
              <span style={{ fontSize: 6, color: "rgba(255,255,255,0.35)", fontFamily: font }}>{p.label}</span>
            </div>
          ))}
        </div>

        {/* DESTINATION BREAKDOWN */}
        <div style={{ paddingBottom: 10, borderBottom: divider }}>
          <div style={{ ...sectionLbl, marginBottom: 6 }}>Applications by Destination</div>
          {[
            { label: "United States",  pct: 64, fill: "linear-gradient(to right, #e6a817, #f59e0b)", pctColor: gold                     },
            { label: "United Kingdom", pct: 24, fill: "rgba(255,255,255,0.35)",                       pctColor: "rgba(255,255,255,0.75)" },
            { label: "Other Countries", pct: 12, fill: "rgba(255,255,255,0.2)",                        pctColor: "rgba(255,255,255,0.5)"  },
          ].map((row, i) => (
            <div key={row.label} style={{ marginBottom: i < 2 ? 8 : 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ fontSize: 8, color: "rgba(255,255,255,0.7)", fontFamily: font }}>{row.label}</span>
                <span style={{ fontSize: 7, color: row.pctColor, fontFamily: font, fontWeight: 600 }}>{row.pct}%</span>
              </div>
              <div style={barTrack}>
                <div style={animBar(row.pct, row.fill)} />
              </div>
            </div>
          ))}
        </div>

        {/* COUNSELOR RATINGS — matching counselor roster rows exactly */}
        <div style={{ paddingBottom: 10, borderBottom: divider }}>
          <div style={{ ...sectionLbl, marginBottom: 6 }}>Counselor Performance</div>
          {[
            { name: "Ms. Carter · 42 students", stars: "★★★★★", rating: "4.9" },
            { name: "Mr. Patel · 38 students",  stars: "★★★★☆", rating: "4.6" },
            { name: "Ms. Wong · 35 students",   stars: "★★★★★", rating: "4.8" },
          ].map((c, i) => (
            <div key={c.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: 6, padding: "6px 8px", background: "rgba(255,255,255,0.02)", marginBottom: i < 2 ? 4 : 0 }}>
              <span style={{ fontSize: 8, color: "rgba(255,255,255,0.75)", fontFamily: font, fontWeight: 500 }}>{c.name}</span>
              <span style={{ fontSize: 7, color: gold, fontFamily: font, fontWeight: 500, flexShrink: 0, marginLeft: 6 }}>{c.stars} {c.rating}</span>
            </div>
          ))}
        </div>

        {/* APPLICATION COMPLETION — matching student essays bars exactly */}
        <div style={{ paddingBottom: 10, borderBottom: divider }}>
          <div style={{ ...sectionLbl, marginBottom: 6 }}>Application Completion</div>
          {[
            { label: "Common App Complete",  pct: 71, fill: "linear-gradient(to right, #e6a817, #f59e0b)" },
            { label: "Personal Statement",   pct: 58, fill: "rgba(255,255,255,0.4)"                       },
          ].map((row, i) => (
            <div key={row.label} style={{ marginBottom: i < 1 ? 8 : 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ fontSize: 7, color: "rgba(255,255,255,0.5)", fontFamily: font }}>{row.label}</span>
                <span style={{ fontSize: 7, color: "rgba(230,168,23,0.9)", fontFamily: font, fontWeight: 600 }}>{row.pct}%</span>
              </div>
              <div style={barTrack}>
                <div style={animBar(row.pct, row.fill)} />
              </div>
            </div>
          ))}
        </div>

        {/* BOTTOM STRIP */}
        <div style={{ borderTop: divider, paddingTop: 8, marginTop: 2 }}>
          {[
            { label: "Apps Submitted",      value: "47", color: gold       },
            { label: "Responses Received",  value: "31", color: "#4ade80"  },
            { label: "In Review",           value: "16", color: "#fbbf24"  },
          ].map((row, i) => (
            <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.06)" : undefined }}>
              <span style={{ fontSize: 7, color: "rgba(255,255,255,0.55)", fontFamily: font }}>{row.label}</span>
              <span style={{ fontSize: 7, color: row.color, fontFamily: font, fontWeight: 600 }}>{row.value}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 5 }}>
            <span style={{ fontSize: 6, color: "rgba(255,255,255,0.55)", fontFamily: font }}>Yield Rate</span>
            <span style={{ fontSize: 8, color: "#4ade80", fontFamily: font, fontWeight: 600 }}>68% accepted</span>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── RoleModal ────────────────────────────────────────────────────────────────

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEnterStudent: () => void;
  onEnterCounselor: () => void;
}

function RoleModal({ isOpen, onClose, onEnterStudent, onEnterCounselor }: RoleModalProps) {
  const prefersReduced = useReducedMotion();
  const [collegeEmail, setCollegeEmail] = useState("");
  const [collegeSubmitted, setCollegeSubmitted] = useState(false);

  // Disabled — waitlist flow replaces modal in early-access mode
  if (true) return null;

  const handleCollegeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!collegeEmail.trim()) return;
    setCollegeSubmitted(true);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          role="dialog"
          aria-modal="true"
          aria-label="Choose your role"
        >
          <motion.div className="absolute inset-0 bg-[#03010a]/80 backdrop-blur-md" onClick={onClose} />
          <motion.div
            className="relative z-10 w-full max-w-[580px] rounded-2xl p-6 sm:p-8"
            style={{
              background: "rgba(13,11,26,0.98)",
              border: "1px solid rgba(255,255,255,0.08)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 40px 100px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
            initial={prefersReduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 16 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-all duration-150"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <h2 className="font-serif text-3xl sm:text-4xl bg-gradient-to-r from-white via-violet-200 to-violet-400 bg-clip-text text-transparent mb-2 leading-tight">
              Which side are you on?
            </h2>
            <p className="font-sans text-sm font-light text-gray-500 mb-7">
              Choose your role to get started with the right experience.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Student */}
              <button
                onClick={() => { onClose(); onEnterStudent(); }}
                className="group flex flex-col gap-4 rounded-xl p-5 text-left transition-all duration-200"
                style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.18)" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(139,92,246,0.1)"; e.currentTarget.style.borderColor = "rgba(139,92,246,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(139,92,246,0.06)"; e.currentTarget.style.borderColor = "rgba(139,92,246,0.18)"; }}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(139,92,246,0.12)" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(167,139,250,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-sans text-sm font-semibold text-white mb-1">Student</h3>
                  <p className="font-sans text-xs leading-relaxed" style={{ color: "rgba(156,163,175,0.8)" }}>I'm applying to colleges</p>
                </div>
                <span className="font-sans text-xs font-medium mt-auto" style={{ color: "rgba(167,139,250,0.9)" }}>Enter as Student →</span>
              </button>

              {/* Counselor */}
              <a
                href="#calendly"
                onClick={(e) => { e.preventDefault(); onClose(); onEnterCounselor(); }}
                className="flex flex-col gap-4 rounded-xl p-5 no-underline transition-all duration-200"
                style={{ background: "rgba(109,40,217,0.06)", border: "1px solid rgba(109,40,217,0.22)" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(109,40,217,0.1)"; e.currentTarget.style.borderColor = "rgba(109,40,217,0.4)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(109,40,217,0.06)"; e.currentTarget.style.borderColor = "rgba(109,40,217,0.22)"; }}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(109,40,217,0.12)" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(167,139,250,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-sans text-sm font-semibold text-white mb-1">Counselor</h3>
                  <p className="font-sans text-xs leading-relaxed" style={{ color: "rgba(156,163,175,0.8)" }}>I guide students</p>
                </div>
                <span className="font-sans text-xs font-medium mt-auto" style={{ color: "rgba(167,139,250,0.9)" }}>Book a Demo →</span>
              </a>

              {/* College */}
              <div
                className="flex flex-col gap-4 rounded-xl p-5"
                style={{ background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.18)" }}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(245,158,11,0.1)" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(245,158,11,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <h3 className="font-sans text-sm font-semibold text-white">College</h3>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)", color: "#F59E0B", fontSize: "9px", fontFamily: "Inter, sans-serif", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 500 }}
                    >
                      <span className="w-1 h-1 rounded-full bg-amber-400 animate-pulse" />
                      Soon
                    </span>
                  </div>
                  <p className="font-sans text-xs leading-relaxed mb-3" style={{ color: "rgba(156,163,175,0.8)" }}>I represent an institution</p>
                  {collegeSubmitted ? (
                    <p className="font-sans text-xs" style={{ color: "rgba(245,158,11,0.85)" }}>You're on the list. We'll be in touch.</p>
                  ) : (
                    <form onSubmit={handleCollegeSubmit} className="flex flex-col gap-2">
                      <input
                        type="email"
                        placeholder="you@university.edu"
                        value={collegeEmail}
                        onChange={(e) => setCollegeEmail(e.target.value)}
                        required
                        className="w-full rounded-lg px-3 py-2 font-sans text-xs text-white placeholder:text-gray-600 focus:outline-none transition-colors duration-150"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(245,158,11,0.35)"; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}
                        aria-label="University email"
                      />
                      <button
                        type="submit"
                        className="w-full py-2 rounded-lg font-sans text-xs font-medium transition-all duration-150"
                        style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)", color: "#F59E0B" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(245,158,11,0.18)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(245,158,11,0.1)"; }}
                      >
                        Join Waitlist →
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default TriptychHero;
